import { useMemo, useState } from "react";
import { InteractiveChartDot } from "./InteractiveChartDot";
import styled from "styled-components";
import {
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DateTime } from "luxon";
import { useDailySalesQuery } from "../../query/adminDailySalesQuery";
import { getMoneyForCurrency } from "../../utils/dailySalesUtils";
import { formatMoney } from "../../utils/formatMoney";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import { LoadingContainer } from "../../components/LoadingContainer";
import { Card } from "../../components/Card";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { useTranslation } from "react-i18next";

const ChartCard = styled(Card)`
  padding: ${spacing.x2}px;
`;

const ChartTitle = styled.h3`
  margin: 0 0 ${spacing.x2}px 0;
  font-size: ${fontSizes.large}px;
`;

const TooltipContent = styled.div`
  padding: ${spacing.x1}px ${spacing.x2}px;
  background: white;
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: ${fontSizes.small}px;

  & > div + div {
    margin-top: 2px;
  }
`;

interface DailySalesChartProps {
  resellerId?: string;
  from: string;
  to: string;
  selectedDate: string;
  onSelectDate: (date: string) => void;
}

export const DailySalesChart = ({
  resellerId,
  from,
  to,
  selectedDate,
  onSelectDate,
}: DailySalesChartProps) => {
  const { t } = useTranslation();
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);
  const { data: stats, isLoading } = useDailySalesQuery(from, to, resellerId);

  const chartData = useMemo(() => {
    const items = stats ?? [];

    return [...items].reverse().map((s) => {
      const date = DateTime.fromISO(s.date);
      const money = getMoneyForCurrency(s.grossAmountByCurrency, Currency.SEK);
      return {
        id: s.date,
        grossAmount: money?.amount ?? 0,
        receiptCount: s.receiptCount,
        name: date.toFormat("d MMM"),
        money,
      };
    });
  }, [stats]);

  if (isLoading && !chartData.length) {
    return (
      <ChartCard>
        <LoadingContainer message={t("statistics.loading")} />
      </ChartCard>
    );
  }

  return (
    <ChartCard>
      <ChartTitle>{t("adminDailySales.dailySalesChart")}</ChartTitle>
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart
          data={chartData}
          margin={{ top: spacing.x2, right: 50, left: 0, bottom: 0 }}
          onMouseMove={(state) => {
            const id = state?.activePayload?.[0]?.payload?.id as
              | string
              | undefined;
            setHoveredDate(id ?? null);
          }}
          onMouseLeave={() => setHoveredDate(null)}
        >
          <XAxis dataKey="name" tick={{ fontSize: fontSizes.small }} />
          <YAxis
            yAxisId="sales"
            tickFormatter={(v) =>
              formatMoney({ amount: v, currency: Currency.SEK })
            }
            tick={{ fontSize: fontSizes.small }}
            width={80}
          />
          <YAxis
            yAxisId="receipts"
            orientation="right"
            tick={{ fontSize: fontSizes.small }}
            width={40}
          />
          <ReferenceLine
            y={0}
            stroke={colors.border}
            strokeWidth={1}
            yAxisId="sales"
          />
          <Legend
            formatter={(value) =>
              value === "receiptCount"
                ? t("adminDailySales.receiptCount")
                : t("adminDailySales.grossSales")
            }
            wrapperStyle={{ fontSize: fontSizes.small }}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const p = payload[0].payload;
              return (
                <TooltipContent>
                  <div>{p.name}</div>
                  {p.money && <div>{formatMoney(p.money)}</div>}
                  <div>
                    {t("adminDailySales.receiptCount")}: {p.receiptCount}
                  </div>
                </TooltipContent>
              );
            }}
            cursor={{ stroke: colors.border, strokeWidth: 1 }}
          />
          <Line
            type="monotone"
            dataKey="grossAmount"
            yAxisId="sales"
            stroke={colors.primary}
            strokeWidth={2}
            dot={(props) => (
              <InteractiveChartDot
                {...props}
                hoveredDate={hoveredDate}
                selectedDate={selectedDate}
                onSelectDate={onSelectDate}
                fill={colors.primary}
                selectedFill={colors.secondary}
              />
            )}
            activeDot={false}
          />
          <Line
            type="monotone"
            dataKey="receiptCount"
            yAxisId="receipts"
            stroke={colors.warning}
            strokeWidth={2}
            dot={(props) => (
              <InteractiveChartDot
                {...props}
                hoveredDate={hoveredDate}
                selectedDate={selectedDate}
                onSelectDate={onSelectDate}
                fill={colors.warning}
                selectedFill={colors.warning}
                interactive={false}
              />
            )}
            activeDot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};
